import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useCommitees } from '@/api/commitee/commitee.queries';
import { ErrorPage } from '@/components/ErrorPage';
import { Spinner } from '@/components/shadcn/Spinner';
import type { AuthContextType } from '@/context/useAuth';
import { useAuth } from '@/context/useAuth';
import { UnauthorizedError } from '@/errors/UnauthorizedError';
import { useCommiteeIdFromUrl } from '@/utils/router';

export const Route = createFileRoute('/$commiteeSlug')({
  beforeLoad: async ({ params, context }) => {
    const { auth } = context as { auth: AuthContextType };
    const commiteeSlug = params.commiteeSlug;

    if (auth.isSessionPending) {
      return { commiteeSlug, isUnauthorized: false };
    }

    const userCommiteeSlug = auth.session?.user.commiteeSlug;

    if (!userCommiteeSlug) {
      return { commiteeSlug, isUnauthorized: false };
    }

    if (commiteeSlug !== userCommiteeSlug) {
      throw new UnauthorizedError();
    }

    return { commitee: userCommiteeSlug };
  },
  component: CommiteeLayout,
  errorComponent: ({ error }) => {
    if (error instanceof UnauthorizedError) {
      return (
        <ErrorPage
          title="Accès non autorisé"
          message="Vous n'avez pas l'autorisation d'accéder à cette entreprise."
        />
      );
    }
    const errorObj = error instanceof Error ? error : new Error(String(error));
    return (
      <ErrorPage
        title="Une erreur est survenue"
        message={errorObj.message || "Une erreur inattendue s'est produite."}
      />
    );
  },
});

function CommiteeLayout() {
  const { session, isSessionPending } = useAuth();
  const { data: commitees, isLoading: isCommiteesLoading } = useCommitees();
  const commiteeId = useCommiteeIdFromUrl();
  const commiteeSlug = Route.useParams().commiteeSlug;

  if (isSessionPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (isCommiteesLoading || !commiteeId) {
    if (!isCommiteesLoading && commitees && commitees.length > 0) {
      return (
        <ErrorPage
          title="Entreprise non trouvée"
          message="L'entreprise demandée n'existe pas ou vous n'y avez pas accès."
        />
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Spinner className="size-8" />
      </div>
    );
  }

  const userCommiteeSlug = session?.user.commiteeSlug;

  if (userCommiteeSlug && commiteeSlug !== userCommiteeSlug) {
    return (
      <ErrorPage
        title="Accès non autorisé"
        message="Vous n'avez pas l'autorisation d'accéder à cette entreprise."
      />
    );
  }

  return <Outlet />;
}
